import { fetchApiJson } from '@vend/utilities'
import * as Ably from 'ably'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useWebRegisterStatusManager } from '../../ng-compat/hooks'
import { getFeatureFromCache } from '../../util/feature-cache'

export type RealtimeCfdContextState = {
  client: Ably.Realtime | null
  serialNumber: string | null
}

export const RealtimeCfdContext = createContext<RealtimeCfdContextState>({
  client: null,
  serialNumber: null,
})

interface RealtimeCfdProviderProps {
  outletId: string
  registerId: string
  children: ReactNode
}

const getTokenUrl = (outletId: string, registerId: string) =>
  `/api/2.0/outlets/${outletId}/lspay/token/${registerId}?pairingData=true`

const getAuthUrl = (registerId: string) =>
  `/api/1.0/cfd/lspay/auth/${registerId}`

export function RealtimeCfdProvider({
  outletId,
  registerId,
  children,
}: RealtimeCfdProviderProps) {
  const featureEnabled = getFeatureFromCache(
    'lspay_customer_facing_display_enabled'
  )
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const isOnline = webRegisterStatusManager?.isOnline() ?? false

  const [client, setClient] = useState<Ably.Realtime | null>(null)
  const [serialNumber, setSerialNumber] = useState<string | null>(null)

  useEffect(() => {
    if (!featureEnabled || !isOnline || !registerId || !outletId) return
    fetchApiJson(getTokenUrl(outletId, registerId)).then(
      ({ serial_number }) => {
        setSerialNumber(serial_number)
      }
    )

    const ablyClient = new Ably.Realtime({
      authUrl: getAuthUrl(registerId),
    })

    setClient(ablyClient)

    return () => {
      ablyClient.close()
    }
  }, [featureEnabled, isOnline, outletId, registerId])

  useEffect(() => {
    if (!isOnline) {
      setClient(null)
    }
  }, [isOnline])

  return (
    <RealtimeCfdContext.Provider value={{ client, serialNumber }}>
      {children}
    </RealtimeCfdContext.Provider>
  )
}

export function useRealtimeCfd(): RealtimeCfdContextState {
  return useContext(RealtimeCfdContext)
}
